import React, { useState } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';

const steps = [
  { number: 1, title: 'Welcome', description: 'Get started' },
  { number: 2, title: 'Working Hours', description: 'Configure availability' },
  { number: 3, title: 'Test Call', description: 'Verify setup' },
  { number: 4, title: 'Complete', description: 'Ready to go' },
];

export default function OnboardingWizard({ currentStep, onComplete, onStepChange }) {
  const { fetchApi } = useCloudAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(false);

  const handleStep1Submit = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use platform default pricing (admin-controlled)
      await fetchApi('/api/cloud/voice/settings', {
        method: 'POST',
        body: JSON.stringify({
          costPerMinute: 0.78,  // Platform default - controlled by admin
          baseFee: 0
        })
      });
      await fetchApi('/api/cloud/auth/services/voice/onboarding', {
        method: 'PUT',
        body: JSON.stringify({ step: 2 })
      });
      onStepChange(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchApi('/api/cloud/auth/services/voice/onboarding', {
        method: 'PUT',
        body: JSON.stringify({ step: 3 })
      });
      onStepChange(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchApi('/api/cloud/auth/services/voice/onboarding', {
        method: 'PUT',
        body: JSON.stringify({ step: 4 })
      });
      onStepChange(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchApi('/api/cloud/auth/services/voice/onboarding', {
        method: 'PUT',
        body: JSON.stringify({ step: 4, completed: true })
      });
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step.number
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {currentStep > step.number ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <span className="mt-2 text-xs text-gray-400">{step.title}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-2 ${currentStep > step.number ? 'bg-purple-600' : 'bg-gray-700'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-300">
          {error}
        </div>
      )}

      <div className="bg-[#111113] rounded-lg border border-gray-800 p-6">
        {currentStep === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-2">Welcome to Voice Agent</h2>
            <p className="text-gray-400 mb-6">Let's get your AI voice assistant set up in just a few steps.</p>
            
            <div className="bg-[#0a0a0b] rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">What's included</h3>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• AI-powered voice conversations</li>
                    <li>• 24/7 automated call handling</li>
                    <li>• Call transcripts and analytics</li>
                    <li>• Custom AI personality and responses</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0b] rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Usage Rate</span>
                <span className="text-white font-medium">$0.78/minute</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Billed based on actual call duration</p>
            </div>

            <button onClick={handleStep1Submit} disabled={loading} className="mt-2 w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg" data-testid="step1-continue-btn">
              {loading ? 'Setting up...' : 'Get Started'}
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-2">Working Hours</h2>
            <p className="text-gray-400 mb-6">Set when your voice agent is available</p>
            <div className="bg-[#0a0a0b] rounded-lg p-4 mb-6 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Enable Working Hours</p>
                <p className="text-sm text-gray-400">Restrict calls to specific times</p>
              </div>
              <button
                onClick={() => setWorkingHoursEnabled(!workingHoursEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${workingHoursEnabled ? 'bg-purple-600' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${workingHoursEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
            {workingHoursEnabled && <p className="text-center text-gray-400 py-4">Schedule configuration coming soon.</p>}
            <button onClick={handleStep2Submit} disabled={loading} className="mt-6 w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg" data-testid="step2-continue-btn">
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-2">Test Call</h2>
            <p className="text-gray-400 mb-6">Verify your voice agent is working correctly</p>
            <div className="bg-[#0a0a0b] rounded-lg p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <p className="text-gray-400">Test call requires Twilio integration.</p>
            </div>
            <button onClick={handleStep3Submit} disabled={loading} className="mt-6 w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg" data-testid="step3-skip-btn">
              {loading ? 'Processing...' : 'Skip for Now'}
            </button>
          </div>
        )}

        {currentStep === 4 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-900/50 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Setup Complete!</h2>
            <p className="text-gray-400 mb-8">Your Voice Agent is ready to use.</p>
            <button onClick={handleComplete} disabled={loading} className="py-3 px-8 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg" data-testid="complete-setup-btn">
              {loading ? 'Finishing...' : 'Go to Dashboard'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
